import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { api } from '../../api/rpc';
import type { User } from '../../api/types';
import { AuthContext } from './authContextDef';
import { flushStatePersistence } from '../../hooks/useStatePersistence';

export { AuthContext, type AuthContextType } from './authContextDef';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On mount: validate stored session instead of using hardcoded "Cached User"
  useEffect(() => {
    const sid = localStorage.getItem('brit_sid');
    if (!sid) {
      setIsLoading(false);
      return;
    }

    api
      .validateSession()
      .then((validatedUser) => {
        setUser(validatedUser);
      })
      .catch(() => {
        // Session invalid — clear it
        localStorage.removeItem('brit_sid');
        setUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setIsLoading(true);
    try {
      const result = await api.login(username, password);
      if (result.records && result.records.length > 0) {
        setUser(result.records[0]);
      } else {
        throw new Error('Login failed: no user returned');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Flush UI state to backend BEFORE session is destroyed
      await flushStatePersistence();
      await api.logout();
    } catch {
      // Ignore logout errors
    } finally {
      setUser(null);
    }
  }, []);

  const updateUser = useCallback((patch: Partial<User>) => {
    setUser((prev) => prev ? { ...prev, ...patch } : prev);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
